'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';

const navLinks = [
  { label: 'How it works', href: '/how-it-works' },
  { label: 'For families', href: '/for-families' },
  { label: 'For clinicians', href: '/for-clinicians' },
  { label: 'Pricing', href: '/pricing' },
];

const footerLinks = [
  {
    group: 'Product',
    items: [
      { label: 'How it works', href: '/how-it-works' },
      { label: 'For families', href: '/for-families' },
      { label: 'For clinicians', href: '/for-clinicians' },
      { label: 'Safety & transparency', href: '/safety' },
      { label: 'Pricing', href: '/pricing' },
    ],
  },
  {
    group: 'Resources',
    items: [
      { label: 'Blog', href: '/blog' },
      { label: 'Limitations', href: '/legal/limitations' },
    ],
  },
  {
    group: 'Legal',
    items: [
      { label: 'Privacy policy', href: '/legal/privacy' },
      { label: 'Terms of service', href: '/legal/terms' },
      { label: 'Limitations', href: '/legal/limitations' },
    ],
  },
];

export function MarketingHeader() {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header suppressHydrationWarning className="sticky top-0 z-30 border-b" style={{ background: 'rgba(13,21,32,0.85)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', borderColor: 'rgba(255,255,255,0.07)' }}>
      <div suppressHydrationWarning className="max-w-5xl mx-auto px-6 h-14 flex items-center justify-between gap-6">
        <Link href="/" className="flex items-center gap-2 flex-shrink-0" style={{ textDecoration: 'none' }}>
          <AppLogo size={28} />
          <span className="font-semibold text-sm tracking-tight" style={{ color: 'rgba(227,234,241,0.95)' }}>Guardian</span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-1" aria-label="Main navigation">
          {navLinks?.map((l) => (
            <Link
              key={l?.href}
              href={l?.href}
              className="px-3 py-1.5 text-sm rounded transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--link)] focus-visible:ring-offset-1"
              style={{ color: 'rgba(163,179,196,0.85)', textDecoration: 'none' }}
            >
              {l?.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Link
            href="/sign-up-login-screen"
            className="hidden sm:inline-block px-3 py-1.5 text-sm rounded transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--link)] focus-visible:ring-offset-1"
            style={{ color: 'rgba(163,179,196,0.85)', textDecoration: 'none' }}
          >
            Sign in
          </Link>
          <Link
            href="/sign-up-login-screen"
            className="px-4 py-1.5 text-sm font-semibold rounded transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--link)] focus-visible:ring-offset-2"
            style={{ background: 'var(--brand-gradient)', color: '#fff', textDecoration: 'none' }}
          >
            Get started
          </Link>

          {/* Mobile menu button */}
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className="md:hidden p-2 min-w-[44px] min-h-[44px] flex items-center justify-center rounded transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--link)]"
            style={{ color: 'rgba(163,179,196,0.85)' }}
            aria-label={mobileOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={mobileOpen}
            aria-controls="mobile-nav"
          >
            {mobileOpen ? (
              <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Mobile nav drawer */}
      {mobileOpen && (
        <nav
          id="mobile-nav"
          className="md:hidden px-4 py-3 space-y-1"
          style={{ borderTop: '1px solid rgba(255,255,255,0.07)', background: 'rgba(13,21,32,0.95)' }}
          aria-label="Mobile navigation"
        >
          {navLinks?.map((l) => (
            <Link
              key={l?.href}
              href={l?.href}
              onClick={() => setMobileOpen(false)}
              className="block px-3 py-2.5 text-sm rounded transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--link)]"
              style={{ color: 'rgba(163,179,196,0.85)', textDecoration: 'none' }}
            >
              {l?.label}
            </Link>
          ))}
          <div className="pt-2 mt-2" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
            <Link
              href="/sign-up-login-screen"
              onClick={() => setMobileOpen(false)}
              className="block px-3 py-2.5 text-sm rounded transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--link)]"
              style={{ color: 'rgba(163,179,196,0.85)', textDecoration: 'none' }}
            >
              Sign in
            </Link>
          </div>
        </nav>
      )}
    </header>
  );
}

export function MarketingFooter() {
  const [year, setYear] = useState(2025);

  useEffect(() => {
    setYear(new Date()?.getFullYear());
  }, []);

  return (
    <footer className="border-t border-[var(--rule)] bg-[var(--surface)]">
      {/* Safety reminder — prominent, not tiny grey text */}
      <div className="bg-[var(--primary)] text-[var(--primary-foreground)]">
        <div className="max-w-5xl mx-auto px-6 py-3 text-center">
          <p className="text-sm font-medium">
            Guardian assists your emergency response — it does not replace it. Guardian cannot contact emergency services on your behalf.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-10">
          <div className="col-span-2 md:col-span-1">
            <Link href="/" className="flex items-center gap-2 mb-3">
              <AppLogo size={24} />
              <span className="font-semibold text-[var(--ink)] text-sm">Guardian</span>
            </Link>
            <p className="text-sm text-[var(--ink-3)] max-w-[200px]">
              Wearable fall and vital-sign detection for older adults, with family circle alerting.
            </p>
          </div>
          {footerLinks?.map((group) => (
            <div key={group?.group}>
              <p className="text-xs font-semibold text-[var(--ink-3)] uppercase tracking-wide mb-3">{group?.group}</p>
              <ul className="space-y-2">
                {group?.items?.map((item) => (
                  <li key={item?.href}>
                    <Link href={item?.href} className="text-sm text-[var(--ink-2)] hover:text-[var(--ink)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--link)] rounded">
                      {item?.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="border-t border-[var(--rule)] pt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <p className="text-xs text-[var(--ink-3)]">
            © {year} Guardian. This is a demonstration build. No real emergency-service connection exists.
          </p>
          <p className="text-xs text-[var(--ink-3)]">
            <Link href="/legal/limitations" className="underline hover:text-[var(--ink)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--link)] rounded">Read limitations</Link>
          </p>
        </div>
      </div>
    </footer>
  );
}
